import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';
import { frontendURL } from './URLHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
  TELEPHONY_REPORTS_ACCESS_PERMISSIONS,
  WHATSAPP_VOICE_REPORTS_ACCESS_PERMISSIONS,
  KANBAN_ACCESS_PERMISSIONS,
  CHAMADOS_VIEW_PERMISSIONS,
  MASSIVA_ACCESS_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import { kanbanRoutePermissions } from './kanbanPermissions';

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [], blockedBy } = {} } =
    route;
  // `blockedBy`: permissão (ou lista) de RESTRIÇÃO do papel personalizado
  // (presença de qualquer uma bloqueia). Admin nunca é barrado por ela.
  if (
    blockedBy &&
    [].concat(blockedBy).some(p => userPermissions.includes(p)) &&
    !userPermissions.includes('administrator')
  ) {
    return false;
  }
  return hasPermissions(routePermissions, userPermissions);
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'dashboard',
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
    { permissions: [TELEPHONY_REPORTS_ACCESS_PERMISSIONS], path: 'telephony' },
    {
      permissions: [WHATSAPP_VOICE_REPORTS_ACCESS_PERMISSIONS],
      path: 'whatsapp-voice/call-reports',
    },
    {
      permissions: kanbanRoutePermissions(KANBAN_ACCESS_PERMISSIONS),
      path: 'kanban',
    },
    { permissions: [CHAMADOS_VIEW_PERMISSIONS], path: 'studio/chamados' },
    { permissions: [MASSIVA_ACCESS_PERMISSIONS], path: 'studio/massiva' },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  // Fallback = perfil, a única rota que não exige permissão de negócio. Mandar
  // pro dashboard aqui era o bug: quem não casa com nenhuma linha acima também
  // não pode ver o dashboard, então o guard recalculava o mesmo destino pra
  // sempre e o vue-router abortava por loop — tela preta, sem diagnóstico.
  return `accounts/${accountId}/${route ? route.path : 'profile/settings'}`;
};

const validateActiveAccountRoutes = (to, user) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  const userPermissions = getUserPermissions(user, to.params.accountId);

  if (routeIsAccessibleFor(to, userPermissions)) return null;

  const redirectPath = defaultRedirectPage(to, userPermissions);

  // Rede de segurança: se o fallback aponta pra rota em que já estamos, o
  // próximo guard recalcula o mesmo destino e o vue-router aborta a navegação
  // por loop infinito, deixando tela preta. Deixa renderizar e grita — o
  // backend continua barrando o que importa.
  if (frontendURL(redirectPath) === to.path) {
    // eslint-disable-next-line no-console
    console.warn(
      `[routeHelpers] Nenhuma rota acessível para as permissões ${JSON.stringify(
        userPermissions
      )}. Renderizando ${to.path} sem redirecionar para não entrar em loop.`
    );
    return null;
  }

  return redirectPath;
};

export const validateLoggedInRoutes = (to, user) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user);
  }

  // If the current account is not active, then redirect the user to the suspended screen
  if (to.name !== 'account_suspended') {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = routeName =>
  routeName === 'notifications_index';
